export type AutoPlannerRuleContainer = {
  "@id"?: string;
  uid?: string;
  weight?: number | null;
  adn?: boolean | null;
  allowStacking?: boolean | null;
  empty?: boolean | null;
  iso?: string | null;
  position?: any[] | null;
  containerType?: {
    teu?: number | null;
    height?: number | null;
    width?: number | null;
    emptyWeight?: number | null;
    name?: string | null;
    abbreviation?: string | null;
  } | null;
  [key: string]: any;
};

export type AutoPlannerRuleViolation = {
  ruleId: string;
  code: string;
  message: string;
};

export type AutoPlannerRuleContext = {
  container: AutoPlannerRuleContainer;
  layer: number;
  x: number;
  y: number;
  span: number;
  supportingContainers: Array<AutoPlannerRuleContainer | undefined>;
};

export type AutoPlannerRule = {
  id: string;
  description: string;
  evaluate: (
    context: AutoPlannerRuleContext
  ) => AutoPlannerRuleViolation | null;
};

export type AutoPlannerRuleEvaluation = {
  allowed: boolean;
  violations: AutoPlannerRuleViolation[];
};

export type TeuStackSupportKind =
  | "floor"
  | "two-1-teu-supports"
  | "same-teu";

export const isAdnCargo = (container?: AutoPlannerRuleContainer) =>
  Boolean(container && !container.dummy && container.adn === true);

/** Keep ADN and regular cargo out of directly adjacent/vertical cells. */
export const respectsAdnSegregation = ({
  container,
  neighboringContainers,
}: {
  container: AutoPlannerRuleContainer;
  neighboringContainers: Array<AutoPlannerRuleContainer | undefined>;
}) =>
  neighboringContainers
    .filter((neighbor) => neighbor && !neighbor.dummy)
    .every((neighbor) => isAdnCargo(neighbor) === isAdnCargo(container));

/**
 * A container may be stacked above another destination only when it is
 * discharged no later than every supporting container. This keeps the cargo
 * below accessible without creating a shifter.
 */
export const avoidsBlockingEarlierDischarge = ({
  container,
  supportingContainers,
  getDischargeOrder,
}: {
  container: AutoPlannerRuleContainer;
  supportingContainers: Array<AutoPlannerRuleContainer | undefined>;
  getDischargeOrder: (container: AutoPlannerRuleContainer) => number | null;
}) => {
  const candidateOrder = getDischargeOrder(container);
  if (candidateOrder === null) return true;

  return supportingContainers
    .filter((support) => support && !support.dummy)
    .every((support) => {
      const supportOrder = getDischargeOrder(support!);
      return supportOrder === null || candidateOrder <= supportOrder;
    });
};

type SupportingContainerLookup = (
  layer: number,
  x: number,
  y: number
) => AutoPlannerRuleContainer | undefined;

export type CohortSpaceRequirement = {
  twentyFt: number;
  fortyFt: number;

  // Number of adjacent 2-TEU floor footprints required.
  twoTeuFootprints: number;

  // Number of additional individual 1-TEU floor cells required.
  oneTeuFootprints: number;

  // How many 20ft containers can be absorbed underneath
  // the 40ft containers in the paired footprints.
  twentyFtUsedInTwoTeuFootprints: number;

  totalFloorCells: number;
};

/**
 * Minimum floor footprints required to stow a destination cohort under the
 * stacking rules (40ft on their paired footprints, stacked one per layer;
 * 20ft in their own single cells, stacked one per layer; every stack one
 * destination).
 *
 * The rule-bound 40ft containers are placed first and claim their paired
 * footprints, which then only ever hold 40ft from the floor up. A 20ft may
 * never sit on a 40ft, so there is no 20ft absorption beneath a 40ft stack:
 * every 20ft needs its own 1-TEU footprint (ceil(twentyFt / layers)).
 */
export const calculateCohortSpaceRequirement = (
  twentyFt: number,
  fortyFt: number,
  maximumLayers: number
): CohortSpaceRequirement => {
  const layers = Math.max(1, Math.floor(maximumLayers));
  const twenty = Math.max(0, Math.floor(twentyFt));
  const forty = Math.max(0, Math.floor(fortyFt));

  // Each paired footprint can hold one 40ft per layer.
  const twoTeuFootprints = forty > 0 ? Math.ceil(forty / layers) : 0;

  // Every 20ft needs its own single cell, one per layer.
  const oneTeuFootprints = Math.ceil(twenty / layers);

  return {
    twentyFt: twenty,
    fortyFt: forty,
    twoTeuFootprints,
    oneTeuFootprints,
    twentyFtUsedInTwoTeuFootprints: 0,
    totalFloorCells: twoTeuFootprints * 2 + oneTeuFootprints,
  };
};

export const getContainerTeu = (container: AutoPlannerRuleContainer) => {
  const teu = Number(container?.containerType?.teu ?? 1);
  return Number.isFinite(teu) && teu > 0 ? teu : 1;
};

/**
 * Container weight in tonnes. The planner stores weights in kilograms, while
 * the vessel stability model (backend + stability tables) works in tonnes.
 */
export const getContainerWeightInTonnes = (
  container: AutoPlannerRuleContainer
) => {
  const weightKg = Number(container?.weight ?? 0);
  return Number.isFinite(weightKg) && weightKg > 0 ? weightKg / 1000 : 0;
};

/**
 * Number of adjacent longitudinal grid cells occupied by a container.
 * In the current planner model 1 TEU occupies one cell, 2 TEU occupies two
 * cells, and the special 4-TEU grid type is represented as a 2x2 block.
 */
export const getContainerGridSpan = (
  container: AutoPlannerRuleContainer
) => {
  const teu = getContainerTeu(container);
  return teu === 4 ? 2 : Math.max(1, Math.round(teu));
};

export const getSupportingContainerLayouts = ({
  layer,
  x,
  y,
  span,
  findContainer,
}: {
  layer: number;
  x: number;
  y: number;
  span: number;
  findContainer: SupportingContainerLookup;
}) => {
  if (layer === 1) return [[]];

  return [
    Array.from({ length: span }, (_, offset) =>
      findContainer(layer - 1, x + offset, y)
    ),
    Array.from({ length: span }, (_, offset) =>
      findContainer(layer - 1, x, y + offset)
    ),
  ];
};

export type StabilityVesselParams = {
  baseOffset?: number | null;
  emptyWeightInTonnes?: number | null;
  emptyCenterOfGravityZ?: number | null;
};

export type StabilityLayer = Array<
  AutoPlannerRuleContainer | undefined | null
>;

/**
 * Loaded vertical center of gravity (KG), in meters, mirroring the backend
 * StabilityCalculator.getVesselLoadedCenterOfGravity:
 *   KG = sum(weight * height) / sum(weight)
 *   - layer 0 is the empty vessel (its own weight at its own KG);
 *   - each container layer contributes its total weight at
 *       cumulativeHeight + 0.5 * averageContainerHeight,
 *     starting from the vessel base offset;
 *   - empty intermediate layers neither add weight nor raise the height.
 */
export const getLoadedVerticalCenterOfGravity = ({
  vessel,
  layers,
  maximumLayers,
}: {
  vessel: StabilityVesselParams;
  layers: StabilityLayer[];
  maximumLayers?: number;
}): number => {
  const baseOffset = Number(vessel?.baseOffset ?? 0.5);
  const emptyWeight = Number(vessel?.emptyWeightInTonnes ?? 1000) || 1000;
  const emptyKg = Number(vessel?.emptyCenterOfGravityZ ?? 1) || 1;

  let totalWeight = emptyWeight;
  let totalMoment = emptyWeight * emptyKg;
  let cumulativeHeight = baseOffset;

  const containerLayerCount = Math.min(
    maximumLayers ?? layers.length,
    layers.length
  );

  for (let layerIndex = 0; layerIndex < containerLayerCount; layerIndex += 1) {
    const layerContainers = (layers[layerIndex] || []).filter(Boolean);
    if (layerContainers.length === 0) continue;

    const layerWeight = layerContainers.reduce(
      (total, container) => total + getContainerWeightInTonnes(container!),
      0
    );
    const heights = layerContainers
      .map((container) => Number(container!.containerType?.height ?? 2.89))
      .filter((height) => Number.isFinite(height) && height > 0);
    if (heights.length === 0) continue;

    const averageHeight =
      heights.reduce((total, height) => total + height, 0) / heights.length;
    totalWeight += layerWeight;
    totalMoment += layerWeight * (cumulativeHeight + averageHeight / 2);
    cumulativeHeight += averageHeight;
  }

  return totalWeight > 0 ? totalMoment / totalWeight : emptyKg;
};

const violation = (
  ruleId: string,
  code: string,
  message: string
): AutoPlannerRuleViolation => ({ ruleId, code, message });

export const getTeuStackSupportKind = ({
  container,
  layer,
  span,
  supportingContainers,
}: AutoPlannerRuleContext): TeuStackSupportKind | null => {
  if (layer === 1) return "floor";

  if (
    supportingContainers.length !== span ||
    supportingContainers.some((support) => !support)
  ) {
    return null;
  }

  const supports = supportingContainers.filter(Boolean);
  const uniqueSupports = Array.from(new Set(supports));
  const candidateTeu = getContainerTeu(container);
  const supportTeus = supports.map(getContainerTeu);

  const isFortyFootOnTwoTwentyFoot =
    candidateTeu === 2 &&
    span === 2 &&
    uniqueSupports.length === 2 &&
    supportTeus.every((supportTeu) => supportTeu === 1);

  if (isFortyFootOnTwoTwentyFoot) return "two-1-teu-supports";

  const isSameSizeStack =
    uniqueSupports.length === 1 &&
    supportTeus.every((supportTeu) => supportTeu === candidateTeu);

  if (isSameSizeStack) return "same-teu";

  return null;
};

/**
 * Enforces aligned corner support for the TEU combinations currently modeled:
 * - equal TEU sizes may be stacked directly;
 * - one 2-TEU container may be supported by two adjacent 1-TEU containers;
 * - a 1-TEU container may not be placed on either half of a 2-TEU container.
 *
 * The placement caller may evaluate more than one adjacent-cell orientation
 * because legacy vessel grids store the two visual cells on either coordinate
 * axis. The compatibility rule itself remains independent of grid orientation.
 *
 * Add future size/ISO compatibility cases here or register an additional rule
 * in DEFAULT_AUTO_PLANNER_RULES without changing the placement algorithm.
 */
export const teuStackCompatibilityRule: AutoPlannerRule = {
  id: "teu-stack-compatibility",
  description: "Container corner supports must align with the tier below.",
  evaluate: ({
    layer,
    supportingContainers,
    ...context
  }: AutoPlannerRuleContext) => {
    if (getTeuStackSupportKind({ layer, supportingContainers, ...context })) {
      return null;
    }

    if (
      supportingContainers.length !== context.span ||
      supportingContainers.some((support) => !support)
    ) {
      return violation(
        "teu-stack-compatibility",
        "missing-support",
        "Every occupied grid cell must be supported by the tier below."
      );
    }

    return violation(
      "teu-stack-compatibility",
      "incompatible-teu-support",
      "This TEU size is not compatible with the containers directly below it."
    );
  },
};

export const DEFAULT_AUTO_PLANNER_RULES: readonly AutoPlannerRule[] = [
  teuStackCompatibilityRule,
];

export const evaluateAutoPlannerRules = (
  context: AutoPlannerRuleContext,
  rules: readonly AutoPlannerRule[] = DEFAULT_AUTO_PLANNER_RULES
): AutoPlannerRuleEvaluation => {
  const violations = rules
    .map((rule) => rule.evaluate(context))
    .filter((result): result is AutoPlannerRuleViolation => Boolean(result));

  return {
    allowed: violations.length === 0,
    violations,
  };
};
